'use client';
import { useEffect, useState } from 'react';
import { formatNumber } from '@witmetrics/utils/strings';
import { useActivePractice, usePracticeCurrency } from '@witmetrics/data-store';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { formatCompactPrice } from '@/utils/metrics';
import { getReportYear } from '@/utils/reports';
import { ACCENT_COLORS } from '@/constants/colors';
import ReportHeader from '@/components/ReportHeader';
import OverviewCard from '@/components/OverviewCard';
import ChartLegend from '@/components/ChartLegend';
import ChartFrame from '@/components/ChartFrame';
import SalesOverviewCard from '@/components/SalesOverviewCard';
import TreatmentRevenueCard from '@/components/TreatmentRevenueCard';
import AcceptedPatientsChart from '@/components/AcceptedPatientsChart';
import RevenueGrowthChart from '@/components/RevenueGrowthChart';
import {
  CHART_HEIGHT as REVENUE_CHART_HEIGHT,
  LEGEND_ITEMS,
} from '@/components/RevenueGrowthChart/constants';
import { CHART_HEIGHT as PATIENTS_CHART_HEIGHT } from '@/components/AcceptedPatientsChart/constants';
import KeyMetricsCard from './KeyMetricsCard';
import TopTreatmentsCard from './TopTreatmentsCard';
import type { OverviewReports } from './types';
import {
  buildKeyMetrics,
  buildKeyMetricsSubtitle,
  buildPeriodTags,
  buildPlaceholderKeyMetrics,
  buildTopTreatments,
  classes,
  fetchOverviewReports,
} from './utils';

export default function Page() {
  const { onApiError } = useAppState();
  const activePractice = useActivePractice();
  const currency = usePracticeCurrency();
  const [reports, setReports] = useState<OverviewReports | null>(null);
  const [isFetchingData, toggleFetchingData] = useToggle(true);

  useEffect(() => {
    if (activePractice) fetchData();
  }, [activePractice]);

  const fetchData = async () => {
    try {
      toggleFetchingData(true);
      setReports(await fetchOverviewReports(activePractice!.id));
      toggleFetchingData(false);
    } catch (err) {
      toggleFetchingData(false);
      onApiError(err, 'Error fetching reports', () => fetchData());
    }
  };

  const renderPatientsLegend = () => {
    if (!reports) return null;
    return (
      <ChartLegend
        items={[
          {
            label: `${getReportYear(reports.current)}`,
            color: ACCENT_COLORS.primary,
          },
          {
            label: `${getReportYear(reports.previous)}`,
            color: ACCENT_COLORS.teal,
          },
        ]}
      />
    );
  };

  const isLoading = isFetchingData || !reports || !currency;
  const periodTags = buildPeriodTags();
  const keyMetrics =
    isLoading || !reports
      ? buildPlaceholderKeyMetrics()
      : buildKeyMetrics(reports);

  return (
    <div className={classes.wrapper}>
      <ReportHeader
        showTimeframe={false}
        title="Overview"
        subtitle="Clinic performance at a glance"
      />
      <div className={classes.section}>
        <KeyMetricsCard
          isFetchingData={isLoading}
          currency={currency}
          subtitle={buildKeyMetricsSubtitle()}
          currentLabel={periodTags.current}
          previousLabel={periodTags.previous}
          rows={keyMetrics}
        />
      </div>
      <div className={classes.section}>
        <OverviewCard
          title="Revenue & Year-over-Year Growth"
          subtitle="Monthly revenue against YoY growth this year"
          actions={<ChartLegend items={LEGEND_ITEMS} />}>
          <ChartFrame isFetchingData={isLoading} height={REVENUE_CHART_HEIGHT}>
            {(onMount) => (
              <RevenueGrowthChart
                currentReport={reports!.current}
                previousReport={reports!.previous}
                onMount={onMount}
              />
            )}
          </ChartFrame>
        </OverviewCard>
      </div>
      <div className={classes.section}>
        <OverviewCard
          title="Accepted patients"
          subtitle="Patients with a case closing date each month"
          actions={renderPatientsLegend()}>
          <ChartFrame isFetchingData={isLoading} height={PATIENTS_CHART_HEIGHT}>
            {(onMount) => (
              <AcceptedPatientsChart
                currentReport={reports!.current}
                previousReport={reports!.previous}
                onMount={onMount}
              />
            )}
          </ChartFrame>
        </OverviewCard>
      </div>
      <div className={classes.treatmentsRow}>
        <TopTreatmentsCard
          isFetchingData={isLoading}
          title="Top treatments by count"
          subtitle="Cases with a closing date this year"
          treatments={
            reports ? buildTopTreatments(reports.current, 'count') : []
          }
          formatValue={(value) => formatNumber(value, 0)}
        />
        <TopTreatmentsCard
          isFetchingData={isLoading}
          fillClassName={classes.revenueFill}
          title="Top treatments by revenue"
          subtitle="Cases with a closing date this year"
          treatments={
            reports ? buildTopTreatments(reports.current, 'revenue') : []
          }
          formatValue={(value) => formatCompactPrice(value, currency)}
        />
      </div>
      <div className={classes.section}>
        <SalesOverviewCard
          isFetchingData={isFetchingData}
          currentReport={reports?.current ?? null}
          previousReport={reports?.previous ?? null}
        />
      </div>
      <div className={classes.section}>
        <TreatmentRevenueCard
          isFetchingData={isFetchingData}
          currentReport={reports?.current ?? null}
          previousReport={reports?.previous ?? null}
        />
      </div>
    </div>
  );
}
